import { useNavigate } from "react-router-dom";
import { Bookmark, BookmarkCheck, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import {
  fingerprintGrant,
  useSavedOpportunities,
  type SavableGrant,
} from "@/hooks/useSavedOpportunities";
import { useSavedItems } from "@/hooks/useSavedItems";
import { supabase } from "@/integrations/supabase/client";

interface SaveGrantButtonProps {
  grant: SavableGrant;
  size?: "sm" | "xs";
}

const SaveGrantButton = ({ grant, size = "xs" }: SaveGrantButtonProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { items, save, markApplied, remove } = useSavedOpportunities();
  const { remove: removeLibrary, refresh: refreshLibrary } = useSavedItems();

  const fp = fingerprintGrant(grant);
  const existing = items.find((i) => i.fingerprint === fp) ?? null;

  const requireAuth = (): boolean => {
    if (!user) {
      navigate("/auth");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!requireAuth()) return;
    if (existing) {
      // Toggle off when in saved state
      if (existing.status === "saved") {
        await remove(grant);
        await removeLibrary({ item_type: "opportunity", item_id: fp });
      } else {
        // Was applied → demote to saved
        await save(grant);
        await mirrorToLibrary();
      }
    } else {
      await save(grant);
      await mirrorToLibrary();
    }
  };

  const handleApplied = async () => {
    if (!requireAuth()) return;
    if (existing?.status === "applied") {
      await remove(grant);
      await removeLibrary({ item_type: "opportunity", item_id: fp });
    } else {
      await markApplied(grant);
      await mirrorToLibrary({ applied: true });
    }
  };

  const mirrorToLibrary = async (opts: { applied?: boolean } = {}) => {
    if (!user) return;
    try {
      await supabase.from("saved_items").upsert(
        {
          user_id: user.id,
          item_type: "opportunity",
          item_id: fp,
          title: grant.title,
          url: grant.url ?? null,
          source: grant.source ?? grant.source_type ?? null,
          summary: grant.description ?? null,
          metadata: {
            amount: grant.amount,
            deadline: grant.deadline,
            source_type: grant.source_type,
            matched_area: grant.matched_area,
            status: opts.applied ? "applied" : "saved",
          } as never,
        },
        { onConflict: "user_id,item_type,item_id" },
      );
      await refreshLibrary();
    } catch {
      /* non-blocking */
    }
  };

  const btnHeight = size === "xs" ? "h-7 px-2.5 text-xs" : "h-8 px-3 text-xs";
  const isSaved = existing?.status === "saved";
  const isApplied = existing?.status === "applied";

  return (
    <div className="flex items-center gap-1.5">
      <Button
        type="button"
        variant={isSaved || isApplied ? "secondary" : "outline"}
        size="sm"
        className={btnHeight}
        onClick={handleSave}
        aria-label={isSaved ? "Unsave grant" : "Save grant"}
      >
        {isSaved || isApplied ? (
          <BookmarkCheck className="h-3.5 w-3.5 mr-1 text-primary" />
        ) : (
          <Bookmark className="h-3.5 w-3.5 mr-1" />
        )}
        {isSaved ? "Saved" : isApplied ? "Saved" : "Save"}
      </Button>
      <Button
        type="button"
        variant={isApplied ? "default" : "outline"}
        size="sm"
        className={`${btnHeight} ${isApplied ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}`}
        onClick={handleApplied}
        aria-label={isApplied ? "Unmark applied" : "Mark as applied"}
      >
        <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
        {isApplied ? "Applied" : "Mark Applied"}
      </Button>
    </div>
  );
};

export default SaveGrantButton;
